import { useEffect, useMemo, useState } from 'react';

// T.Z113 — the shared fold of the two «Провалил ЕВИ» facts (one chip, either source).
import { eviFailedChip } from '@cb-manager/shared';

import type { ApplicationDto, ApplicationListQuery } from '../../api/applications';
import { useApplications } from '../../api/applications';
import { ExportButton } from '../../components/ExportButton';
import { Badge, Button, Drawer, EmptyState, ErrorState, Select } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams, type ParamValue } from '../../lib/query-params';
import { formatDateTime, MISSING } from '../dashboard/format';
import { ApplicationsFilterBar } from './ApplicationsFilterBar';
import { ApplicationProfile } from './ApplicationProfilePage';
import { UnlinkedAlert } from './UnlinkedAlert';
import {
  exportColumnsParam,
  readVisibleColumns,
  resetVisibleColumns,
  toggleVisibleColumn,
  visibleColumnsOf,
  writeVisibleColumns,
} from './applications-columns';
import { RegisterColumnsControl } from './RegisterColumnsControl';
import {
  EVI_DEADLINE_DAYS,
  FORMAT_LABEL,
  PARAM,
  PAGE_SIZE_OPTIONS,
  REGISTER_HEADERS,
  ageCellText,
  clampPage,
  CONTRACT_STATE_TONE,
  CONTRACT_DEADLINE_CHIP_TITLE,
  ENROLL_STATE_LABEL,
  ENROLL_STATE_TONE,
  contractCellText,
  contractDeadlineView,
  daysCellText,
  difficultyCellText,
  gradeCellText,
  EVI_FAILED_CHIP_TITLE,
  displayStatusView,
  eviDeadline,
  exportApplicationsQuery,
  filtersToQuery,
  formatSortState,
  pageCount,
  parseFilters,
  parsePguId,
  rowKey,
  sortArrow,
  transfertCellText,
  toggleSort,
  type ContractDeadlineView,
  type EviDeadline,
  type RegisterColumn,
  type SortField,
  type SortOrder,
} from './applications-model';
import { managerStatusCellLine } from './manager-statuses-model';

import './applications.css';

/**
 * Applications register (И3, task T.C3).
 *
 * The whole view lives in the URL: every filter, the sort and the page are query
 * params (the dashboard's rule), so a refresh or a shared link restores the slice.
 * The api filters, sorts and paginates (contract #3) — the table only renders.
 * A row opens the profile as a drawer on the same URL (`?pguId=…`), so closing it
 * brings the register back exactly as it was.
 *
 * A4/T.Z19 (owner, 2026-09-13): the «Флаги» column is gone — the owner did not
 * recognise it, and the three states it showed live on elsewhere (the profile
 * card's badges and toggles, the export's own «Флаги» column). The column set is
 * `REGISTER_HEADERS` in the model, the same list the skeleton prints.
 *
 * T.Z20b: the red «несвязанные ученики» alert sits between the header and the
 * card, above every filter — the one condition the owner wants surfaced before a
 * manager reads anything else on this page ({@link UnlinkedAlert}).
 *
 * T.Z59: the «Сортировка» selectbox is gone — every column HEADER is the sort control
 * now. A click asks the api (#3 `sortBy`/`order`) to sort the WHOLE filtered set before
 * the page is cut, so page 1 shows the globally smallest/largest row; the state lives
 * in the URL (`?sortBy=…&order=…`) like every other filter, and the active column
 * carries the arrow.
 */
export function ApplicationsPage() {
  const { params, setParams, reset } = useQueryParams();

  const filters = useMemo(() => parseFilters(params), [params]);
  const query = useMemo(() => filtersToQuery(filters), [filters]);
  const drawerPguId = parsePguId(params[PARAM.PGU_ID]);

  const list = useApplications(query);

  // A filter change restarts the list at page 1: page 3 of a narrower result is
  // an empty screen the manager did not ask for.
  const setFilter = (key: string, value: ParamValue) =>
    setParams(key === PARAM.PAGE || key === PARAM.PAGE_SIZE ? { [key]: value } : { [key]: value, [PARAM.PAGE]: undefined });

  // ADDITIVE (v1.8.0, Р9/#3, T.Y10) — several filters in ONE URL write (the manager
  // status swap needs it: two `setFilter` calls would each rebuild the URL from the
  // same snapshot and the second would drop the first).
  const setFilterPatch = (patch: Record<string, ParamValue>) =>
    setParams({ ...patch, [PARAM.PAGE]: undefined });

  const setPage = (page: number) => setParams({ [PARAM.PAGE]: page > 1 ? page : undefined });
  const setPageSize = (size: number) => setParams({ [PARAM.PAGE_SIZE]: size !== 50 ? size : undefined, [PARAM.PAGE]: undefined });

  // T.Z59 — one header click, one URL write: a new column starts ascending (the api's
  // own default), the active column flips. The page restarts at 1 like after any filter,
  // and a click that lands on the contract default empties BOTH keys — so the default
  // view's URL (and request) stay exactly as they were before T.Z59.
  const toggleColumnSort = (field: SortField) => {
    const next = formatSortState(toggleSort(filters.sort, field));
    setFilterPatch(
      next ? { [PARAM.SORT_BY]: next.sortBy, [PARAM.ORDER]: next.order } : { [PARAM.SORT_BY]: undefined, [PARAM.ORDER]: undefined },
    );
  };

  const rows = list.data?.data.applications ?? [];
  const meta = list.data?.meta;
  const total = meta?.total ?? 0;
  const pages = pageCount(total, filters.pageSize);
  // The api clamps a page that ran past the end; the URL follows on the next render.
  const page = clampPage(filters.page, total, filters.pageSize);
  // One "now" per fetch: every deadline chip of a page is judged by the same instant,
  // and the chip does not tick while the data on screen is the same.
  const now = useMemo(() => new Date(), [list.dataUpdatedAt]);

  // T.Z219 — the visible columns. PURELY PRESENTATIONAL: #3 still answers every
  // field of every row (and #18 keeps its own sheet columns); hiding removes the
  // header, the cells and the sort control from the SCREEN only. The state is not
  // a filter — it lives in `localStorage` (versioned key), not the URL, and the
  // T.Z192 applied tint deliberately does not apply.
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(() => readVisibleColumns(window.localStorage));
  useEffect(() => {
    writeVisibleColumns(window.localStorage, visibleColumnIds);
  }, [visibleColumnIds]);
  const renderedColumns = useMemo(() => visibleColumnsOf(REGISTER_HEADERS, visibleColumnIds), [visibleColumnIds]);
  const showColumn = useMemo(() => new Set(renderedColumns.map((column) => column.id)), [renderedColumns]);
  const toggleColumnVisibility = (id: string) => setVisibleColumnIds((current) => toggleVisibleColumn(current, id));
  const resetColumnVisibility = () => setVisibleColumnIds(resetVisibleColumns());

  // The venue filter's fallback vocabulary: the names of the venues the loaded
  // page actually shows, so a scoped `?venueId=` the directory does not know
  // still prints a name instead of reading as «nothing selected».
  const venueNames = useMemo(() => {
    const names = new Map<number, string>();
    for (const row of rows) {
      if (row.venueId !== null && row.venueName !== null) names.set(row.venueId, row.venueName);
    }
    return names;
  }, [rows]);

  const openProfile = (application: ApplicationDto) => {
    if (application.pguId === null) return; // the FTP-only row has no ЕПГУ key to ask for
    setParams({ [PARAM.PGU_ID]: application.pguId });
  };

  const closeProfile = () => setParams({ [PARAM.PGU_ID]: undefined });

  return (
    <>
      <div className="applications-page">
        <div className="page-header">
          <div>
            <h1>Заявления</h1>
            <p className="page-header__sub">Реестр заявлений ЕПГУ: фильтры, статусы и профиль заявления.</p>
          </div>
          {/* T.E1 — the register as a file, with the filters that are on screen. The
              pagination keys never travel: an export is the whole filtered set.
              T.Z220 — and with the COLUMNS that are on screen: the T.Z219 visible ids
              become `?columns=` when something is hidden (read through the model, not
              raw localStorage). */}
          <ExportButton
            resource="applications"
            testId="applications-export"
            params={exportApplicationsParams(query, visibleColumnIds)}
          />
        </div>

        {/* T.Z20b — the offline applications with no площадка, counted live by the
            same `#3` slices the click answers. Absent when there is nothing to act
            on, and never shown to a role that cannot act on it. */}
        <UnlinkedAlert />

        <div className="card card--flush applications-card">
          <ApplicationsFilterBar
            params={params}
            onFilter={setFilter}
            onFilterPatch={setFilterPatch}
            onReset={reset}
            venueNames={venueNames}
            columnsControl={
              <RegisterColumnsControl
                columns={REGISTER_HEADERS}
                visible={visibleColumnIds}
                onToggle={toggleColumnVisibility}
                onReset={resetColumnVisibility}
              />
            }
          />

          {list.isPending ? (
            <TableSkeleton columns={renderedColumns} />
          ) : list.isError ? (
            <div className="filter-bar__meta">
              <ErrorState
                message={list.error instanceof Error ? list.error.message : 'Не удалось загрузить заявления.'}
                onRetry={() => void list.refetch()}
              />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="Заявлений не найдено"
              hint={
                hasActiveFilterState(filters)
                  ? 'По этим фильтрам ничего нет — ослабьте условие или сбросьте фильтры.'
                  : 'Заявления появятся после первого снапшота ЕПГУ или ЕВИ.'
              }
              action={
                hasActiveFilterState(filters) ? (
                  <Button size="sm" data-testid="empty-reset" onClick={reset}>
                    Сбросить фильтры
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <div className="filter-bar__meta">
                <span data-testid="applications-total">
                  {total} заявл.
                </span>
                <span data-testid="applications-page" aria-live="polite">
                  Страница {page} из {pages}
                </span>
                {list.isFetching && !list.isPending ? <span aria-hidden="true">обновление…</span> : null}
              </div>

              <TableWrap>
                <table className="data" data-testid="applications-table">
                  <thead>
                    <tr>
                      {/* T.Z219 — the VISIBLE columns only; a hidden sortable header
                          simply does not exist while hidden, so it cannot be clicked. */}
                      {renderedColumns.map((column) =>
                        // `sortable: false` renders a plain title — the T.Z101 escape
                        // hatch for a column the api's `?sortBy=` vocabulary does not
                        // know yet (a control that 400s is worse than a title). Holds
                        // two columns since v1.9.87 (T.Z237): «Уровень сложности
                        // (УНТИ)» and «Трансферт» — words with no asked order.
                        column.sortable === false ? (
                          <th key={column.id} className={column.num ? 'num' : undefined}>
                            {column.label}
                          </th>
                        ) : (
                          <SortableHeader
                            key={column.id}
                            column={column.id as SortField}
                            label={column.label}
                            num={column.num}
                            arrow={sortArrow(column.id as SortField, filters.sort)}
                            onToggle={toggleColumnSort}
                          />
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((application) => {
                      const addressable = application.pguId !== null;
                      const deadline = eviDeadline(application, now);
                      // ADDITIVE (v1.9.46, T.Z165) — the api spent the request instant;
                      // the cell is a pure function of the row, never of the clock.
                      const contractDeadline = contractDeadlineView(application);
                      return (
                        <tr
                          key={rowKey(application)}
                          className={addressable ? 'applications-row' : undefined}
                          data-testid="applications-row"
                          data-pgu-id={application.pguId ?? ''}
                          tabIndex={addressable ? 0 : undefined}
                          onClick={addressable ? () => openProfile(application) : undefined}
                          onKeyDown={
                            addressable
                              ? (event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    openProfile(application);
                                  }
                                }
                              : undefined
                          }
                        >
                          {/* T.Z219 — every cell below is wrapped in its column's
                              visibility: a hidden column contributes no `<td>` at all,
                              so header and rows always have the same width. The values
                              still arrive from the api (presentation only). */}
                          {showColumn.has('pguId') && (
                            <td className="cell-primary">
                              {addressable ? (
                                <button
                                  type="button"
                                  className="link-btn"
                                  data-testid="application-link"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openProfile(application);
                                  }}
                                >
                                  {application.pguId}
                                </button>
                              ) : (
                                <span className="cell-muted" title="Заявление только из УНТИ — без ЕПГУ ID">
                                  {MISSING}
                                </span>
                              )}
                            </td>
                          )}
                          {showColumn.has('fullName') && <td data-testid="row-full-name">{application.fullName ?? MISSING}</td>}
                          {showColumn.has('phone') && <td data-testid="row-phone">{application.phone ?? MISSING}</td>}
                          {showColumn.has('email') && <td data-testid="row-email">{application.email ?? MISSING}</td>}
                          {showColumn.has('status') && (
                            <td>
                              <div className="status-cell">
                                {/* T.Z143 (v1.9.36) — the badge IS the display status now:
                                    «Провалил ЕВИ» when the fact holds, so the owner reads a
                                    status, not a flag; the selectbox filters the same value. */}
                                <StatusBadge
                                  status={application.status}
                                  eviFailed={application.eviFailed}
                                  eviFailedManually={application.eviFailedManually}
                                  linkReceivedAt={application.linkReceivedAt}
                                  eviPassedAt={application.eviPassedAt}
                                />
                                {/* ADDITIVE (v1.9.25, T.Z113) — «Провалил ЕВИ», ONE chip for
                                    either source (the auto fact the worker writes + the T.Z96
                                    manual one), folded by the shared `eviFailedChip`. The
                                    badge above now speaks the same status; the chip stays the
                                    fact's own surface, with the «(вручную)» wording when only
                                    the human said it. */}
                                <EviFailedChip
                                  eviFailed={application.eviFailed}
                                  eviFailedManually={application.eviFailedManually}
                                />
                                <ManagerStatusLine label={managerStatusCellLine(application.managerStatus)} />
                              </div>
                            </td>
                          )}
                          {/* ADDITIVE (v1.9.15, T.Z86) — the age of the badge above it. */}
                          {showColumn.has('daysInStatus') && (
                            <td
                              data-testid="row-days-in-status"
                              className="num"
                              title="Дней в текущем статусе"
                            >
                              {daysCellText(application.daysInStatus)}
                            </td>
                          )}
                          {showColumn.has('venue') && (
                            <td className={application.venueName ? undefined : 'cell-muted'}>
                              {application.venueName ?? MISSING}
                            </td>
                          )}
                          {/* ADDITIVE (v1.9.94, T.Z247) — the distribution block: the
                              админка's «Школа» / «Класс/Группа» / «Педагог», folded by
                              the api off the child's LIVE study-group link. «—» on all
                              three = «не распределён»; the api leaves the keys off on
                              surfaces that do not read the link, the optional chain
                              keeps such a payload rendering «—» anyway. */}
                          {showColumn.has('groupVenueName') && (
                            <td data-testid="row-group-school" className={application.groupVenueName ? undefined : 'cell-muted'}>
                              {application.groupVenueName ?? MISSING}
                            </td>
                          )}
                          {showColumn.has('groupName') && (
                            <td data-testid="row-group-name" className={application.groupName ? undefined : 'cell-muted'}>
                              {application.groupName ?? MISSING}
                            </td>
                          )}
                          {showColumn.has('groupTeacherName') && (
                            <td data-testid="row-group-teacher" className={application.groupTeacherName ? undefined : 'cell-muted'}>
                              {application.groupTeacherName ?? MISSING}
                            </td>
                          )}
                          {showColumn.has('courseId') && <td className="num">{application.courseId ?? MISSING}</td>}
                          {showColumn.has('submittedAt') && (
                            <td data-testid="row-submitted-at" className="cell-compact">
                              {formatDateTime(application.submittedAt)}
                            </td>
                          )}
                          {showColumn.has('classCourse') && (
                            <td data-testid="row-class-course" className="num">
                              {application.classCourse ?? MISSING}
                            </td>
                          )}
                          {showColumn.has('format') && (
                            <td data-testid="row-format" className={application.format ? undefined : 'cell-muted'}>
                              {application.format === null ? MISSING : FORMAT_LABEL[application.format]}
                            </td>
                          )}
                          {showColumn.has('eviDeadline') && (
                            <td data-testid="row-evi-deadline">
                              {deadline === null ? (
                                <span className="cell-muted">{MISSING}</span>
                              ) : (
                                <DeadlineChip deadline={deadline} />
                              )}
                            </td>
                          )}
                          {/* ADDITIVE (v1.9.15, T.Z88) — days since the ЕВИ PASS, not the
                              current status: it keeps counting after the status moves on.
                              The «>7 дней» colouring is T.Z87's договор work, not here. */}
                          {showColumn.has('daysSinceEvi') && (
                            <td data-testid="row-days-since-evi" className="num" title="Дней с момента сдачи ЕВИ">
                              {daysCellText(application.daysSinceEvi)}
                            </td>
                          )}
                          {/* ADDITIVE (v1.9.48, T.Z168) — «Возраст»: the whole-year age the
                              api derived over the page's one instant (`age`, «—» when the
                              feed has not touched the row yet). The birth date itself never
                              reaches the browser — only this number does. */}
                          {showColumn.has('age') && (
                            <td data-testid="row-age" className="num" title="Полных лет на текущий день">
                              {ageCellText(application.age ?? null)}
                            </td>
                          )}
                          {/* ADDITIVE (v1.9.21, T.Z101) — the contract fact, next to the
                              ЕВИ clock: passed long ago + no contract = the обзвон list.
                              `null` = the sync has not spoken about the row («—»).
                              T.Z105 — the fact is a STATUS PILL now, the same `<Badge>`
                              chrome the derived-status column pills with and the same
                              green/red the dashboard's contract funnel inks; a row with
                              no fact stays the quiet dash, never a pill. */}
                          {showColumn.has('contract') && (
                            <td
                              data-testid="row-contract"
                              data-contract={application.contractState ?? 'none'}
                              data-enroll={application.enrollState ?? 'none'}
                            >
                              {application.contractState === null && application.enrollState === null ? (
                                // Neither axis has spoken about the row: the one quiet «—».
                                <span className="cell-muted">{MISSING}</span>
                              ) : (
                                <>
                                  {application.contractState !== null && (
                                    <Badge tone={CONTRACT_STATE_TONE[application.contractState]}>
                                      {contractCellText(application)}
                                    </Badge>
                                  )}
                                  {application.enrollState !== null && (
                                    <Badge tone={ENROLL_STATE_TONE[application.enrollState]}>
                                      {ENROLL_STATE_LABEL[application.enrollState]}
                                    </Badge>
                                  )}
                                </>
                              )}
                            </td>
                          )}
                          {/* ADDITIVE (v1.9.46, T.Z165) — «Дедлайн договора», the clock of
                              the contract fact in the cell to the left: the manager's
                              prioritisation surface Юлия Дмиткина asked for. The api owns
                              the day math (`contractDeadlineAt` + `contractDaysLeft`, one
                              instant per page); the cell only formats. Signed renders the
                              calm signing day, «—» stays plain: no clock, no deadline. */}
                          {showColumn.has('contractDeadline') && (
                            <td data-testid="row-contract-deadline" data-deadline={contractDeadline?.tone ?? 'none'}>
                              {contractDeadline === null ? (
                                <span className="cell-muted">{MISSING}</span>
                              ) : (
                                <ContractDeadlineChip view={contractDeadline} />
                              )}
                            </td>
                          )}
                          {/* ADDITIVE (v1.9.77, T.Z218) — the УНТИ entry exam block, the
                              same five keys the export appends after «Возраст»: the level
                              (RU label fold), the three grades and their sum, all read off
                              the row's leaf by the api (the same winner the «Дедлайн
                              договора» clock uses). «—» where the mirror holds no fact —
                              never a guessed 0. ADDITIVE (v1.9.78, T.Z219) — each grade
                              header sorts its own column (`sortBy=<grade>`). */}
                          {showColumn.has('difficultyLevel') && (
                            <td
                              data-testid="row-difficulty-level"
                              className={application.difficultyLevel ? undefined : 'cell-muted'}
                            >
                              {difficultyCellText(application.difficultyLevel ?? null)}
                            </td>
                          )}
                          {showColumn.has('algorithmGrade') && (
                            <td data-testid="row-algorithm-grade" className="num">
                              {gradeCellText(application.algorithmGrade ?? null)}
                            </td>
                          )}
                          {showColumn.has('languageGrade') && (
                            <td data-testid="row-language-grade" className="num">
                              {gradeCellText(application.languageGrade ?? null)}
                            </td>
                          )}
                          {showColumn.has('motivationGrade') && (
                            <td data-testid="row-motivation-grade" className="num">
                              {gradeCellText(application.motivationGrade ?? null)}
                            </td>
                          )}
                          {showColumn.has('gradeSum') && (
                            <td data-testid="row-grade-sum" className="num">
                              {gradeCellText(application.gradeSum ?? null)}
                            </td>
                          )}
                          {/* ADDITIVE (v1.9.87, T.Z237) — «Трансферт»: the leaf-winner's
                              own word, VERBATIM (their vocabulary is never translated,
                              never re-cased), «—» where the mirror has no winner row or
                              its word is NULL. The api resolves it with the same
                              batched fold as the exam keys; the cell only prints. */}
                          {showColumn.has('transfertStatus') && (
                            <td
                              data-testid="row-transfert"
                              className={application.transfertStatus ? undefined : 'cell-muted'}
                            >
                              {transfertCellText(application.transfertStatus ?? null)}
                            </td>
                          )}
                          {showColumn.has('updatedAt') && <td data-testid="row-updated">{formatDateTime(application.updatedAt)}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </TableWrap>

              <Pagination
                page={page}
                pages={pages}
                total={total}
                pageSize={filters.pageSize}
                onPage={setPage}
                onPageSize={setPageSize}
              />
            </>
          )}
        </div>
      </div>

      {drawerPguId !== null ? (
        <Drawer
          open
          onClose={closeProfile}
          title={`Заявление ${drawerPguId}`}
          description="Профиль, источники полей и история изменений."
        >
          <ApplicationProfile pguId={drawerPguId} onBack={closeProfile} />
        </Drawer>
      ) : null}
    </>
  );
}

/**
 * The export query of the view on screen (#18) — the pure half lives in the model
 * (`exportApplicationsQuery`: pagination dropped, every filter rides). The ЕПГУ
 * format filter travels under its export-only key `appFormat` (`format` on #18 is
 * the FILE format); the file-format token itself is the button's job
 * (`exportParams`), not the page's.
 *
 * T.Z99 (v1.9.19), AMENDED v1.9.79 (T.Z220) — the «Формат не указан» literal now
 * TRAVELS: `appFormat` takes `none` (the api's parity pass), so a bucket-filtered
 * view exports filtered at last — before this it exported UNFILTERED by format, a
 * documented honest drop the file==screen rule retired. And the T.Z219 column
 * visibility rides as `?columns=` whenever the view hides something (the pure
 * `exportColumnsParam`, read through the model — nothing hidden → no parameter, the
 * request stays exactly what it was).
 */
function exportApplicationsParams(
  query: ApplicationListQuery,
  visibleColumnIds?: ReadonlyArray<string>,
): Record<string, string | number | boolean | undefined> {
  const { format, ...filters } = exportApplicationsQuery(query);
  const appFormat = format !== undefined ? { appFormat: format } : {};
  const columns = visibleColumnIds === undefined ? {} : { columns: exportColumnsParam(visibleColumnIds) };
  return { ...filters, ...appFormat, ...columns };
}

/**
 * T.Z59 — one clickable column header. The id IS the `?sortBy=` value (the column set
 * is the sort vocabulary), the arrow marks the active column and its direction, and a
 * muted idle marker tells the rest apart from plain titles. `aria-sort` carries the
 * same information for a screen reader, on the `th` where the spec puts it.
 */
function SortableHeader({
  column,
  label,
  num,
  arrow,
  onToggle,
}: {
  column: SortField;
  label: string;
  num?: boolean;
  arrow: SortOrder | null;
  onToggle: (field: SortField) => void;
}) {
  return (
    <th
      className={num ? 'num' : undefined}
      aria-sort={arrow === 'asc' ? 'ascending' : arrow === 'desc' ? 'descending' : undefined}
    >
      <button
        type="button"
        className={arrow ? 'th-sort th-sort--active' : 'th-sort'}
        data-testid={`sort-${column}`}
        data-order={arrow ?? undefined}
        title={`Сортировать по «${label}»`}
        onClick={() => onToggle(column)}
      >
        <span>{label}</span>
        <span className={arrow ? 'th-sort__arrow' : 'th-sort__arrow th-sort__arrow--idle'} aria-hidden="true">
          {arrow === 'asc' ? '↑' : arrow === 'desc' ? '↓' : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * The «Статус» badge of a row (T.Z143): the row's DISPLAY status — the stored status,
 * «Провалил ЕВИ» when either fact source holds, or (T.Z204) «Не прошёл ЕВИ» when the
 * ЕВИ deadline of a LINK_RECEIVED row has lapsed with no pass — exactly the value the
 * «Статус» selectbox and the `?status=` virtual tokens speak. The clock is the render
 * instant (`displayStatusView`'s default `now`), the same one the «Дедлайн ЕВИ» cell
 * beside it is read by. `data-status` carries the display value for the tests; the
 * badge title names WHY (the fact's SOURCE, the lapsed clock), so the fold never
 * renders an unexplained red pill.
 */
function StatusBadge({
  status,
  eviFailed,
  eviFailedManually,
  linkReceivedAt,
  eviPassedAt,
}: {
  status: ApplicationDto['status'];
  eviFailed: boolean;
  eviFailedManually: boolean;
  linkReceivedAt: ApplicationDto['linkReceivedAt'];
  eviPassedAt: ApplicationDto['eviPassedAt'];
}) {
  const view = displayStatusView({ status, eviFailed, eviFailedManually, linkReceivedAt, eviPassedAt });
  return (
    <span data-testid="row-status" data-status={view.status}>
      <Badge tone={view.tone} title={view.title}>
        {view.label}
      </Badge>
    </span>
  );
}

/**
 * The «Статус» cell's second line (ADDITIVE v1.8.2, Р9, T.Z17): the row's manager
 * status as resolved by the api, smaller than the system badge above it. `null`
 * renders nothing — an unassigned row keeps the pre-1.8.2 cell, and the wording
 * «без статуса» stays the profile block's business. The line is live: the #5.6
 * write invalidates the register root, so a status changed in the open drawer
 * lands here without a reload (`managerStatusWriteInvalidationKeys`).
 */
/**
 * T.Z113 — the «Статус» cell's fact chip: «Провалил ЕВИ» (the AUTO fact — the child
 * vanished from the fresh ЕВИ pass list with no FTP evidence) or «Провалил ЕВИ
 * (вручную)» when only the T.Z96 human verdict holds. The shared fold decides both
 * WHETHER the chip renders and WHICH caption it prints, so the register and the card
 * cannot disagree. Red ink (`--status-expired-*`) — the same tone the contract
 * funnel's red sections and the REJECTED badge use: a queue that needs a person.
 * Renders nothing when neither fact holds.
 */
function EviFailedChip({
  eviFailed,
  eviFailedManually,
}: {
  eviFailed: boolean;
  eviFailedManually: boolean;
}) {
  const chip = eviFailedChip({ eviFailed, eviFailedManually });
  if (chip === null) return null;
  return (
    <span className="manager-status-chip evi-failed-chip" data-testid="row-evi-failed" title={EVI_FAILED_CHIP_TITLE}>
      {chip.label}
    </span>
  );
}

function ManagerStatusLine({ label }: { label: string | null }) {
  if (label === null) return null;
  return (
    <span
      className="manager-status-chip"
      data-testid="row-manager-status"
      title="Статус менеджера — меняется в карточке заявления"
    >
      {label}
    </span>
  );
}

/**
 * «Дедлайн ЕВИ» (T.F1): `linkReceivedAt + 10 дней`, shown only where the clock is
 * running (LINK_RECEIVED). Overdue turns the chip red — that is the row a manager
 * has to chase, so the colour is the information, not decoration.
 */
function DeadlineChip({ deadline }: { deadline: EviDeadline }) {
  return (
    <span
      data-testid="evi-deadline"
      className={deadline.overdue ? 'deadline-chip deadline-chip--overdue' : 'deadline-chip'}
      data-overdue={deadline.overdue ? 'true' : 'false'}
      title={`Ссылка отправлена + ${EVI_DEADLINE_DAYS} дней = ${formatDateTime(deadline.at)}`}
    >
      {deadline.label}
    </span>
  );
}

/**
 * ADDITIVE (v1.9.46, T.Z165) — the «Дедлайн договора» cell content, the tone IS the
 * information the way the «Дедлайн ЕВИ» chip above paints it: a running clock pills
 * warn («через N дн.») or bad — the deadline is today, or already past («просрочен на
 * N дн.») — and a signed row renders the calm muted signing day, never a second green
 * pill next to the «Договор» one. The rule travels in the `title`, not in the cell.
 */
function ContractDeadlineChip({ view }: { view: ContractDeadlineView }) {
  if (view.tone === 'signed') {
    return (
      <span className="cell-muted" title="Договор подписан — дедлайна больше нет">
        {view.label}
      </span>
    );
  }
  return (
    <Badge tone={view.tone === 'danger' ? 'bad' : 'warn'} title={CONTRACT_DEADLINE_CHIP_TITLE}>
      {view.label}
    </Badge>
  );
}

function Pagination({
  page,
  pages,
  total,
  pageSize,
  onPage,
  onPageSize,
}: {
  page: number;
  pages: number;
  total: number;
  pageSize: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}) {
  return (
    <div className="pagination" data-testid="applications-pagination">
      <span data-testid="pagination-label">
        Страница {page} из {pages} · {total} всего
      </span>
      <span className="pagination__pages">
        <Button size="sm" data-testid="page-prev" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          ← Назад
        </Button>
        <Button size="sm" data-testid="page-next" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Вперёд →
        </Button>
      </span>
      <label className="pagination__size">
        <span className="filter-bar__label">Строк на странице</span>
        <Select
          data-testid="filter-pageSize"
          aria-label="Строк на странице"
          value={String(pageSize)}
          onChange={(event) => onPageSize(Number(event.target.value))}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
      </label>
    </div>
  );
}

/**
 * Skeleton of the exact surface it stands in for — the filter bar stays mounted
 * (it is interactive during a load), only the table is a placeholder.
 * T.Z219 — it takes the VISIBLE columns, so the skeleton and the loaded table can
 * never disagree about how wide the table is.
 */
function TableSkeleton({ columns }: { columns: ReadonlyArray<RegisterColumn> }) {
  return (
    <div className="table-wrap" data-testid="applications-skeleton">
      <table className="data" aria-hidden="true">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.id} className={column.num ? 'num' : undefined}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td key={column.id}>
                  <div className="skeleton skeleton--line" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Narrowed for the empty-state hint (the model's `hasActiveFilters`, page-aware). */
function hasActiveFilterState(filters: ReturnType<typeof parseFilters>): boolean {
  return (
    filters.status !== undefined ||
    filters.venueId !== undefined ||
    filters.courseId !== undefined ||
    filters.region !== undefined ||
    filters.q !== undefined ||
    filters.hasVenue !== undefined ||
    filters.format !== undefined ||
    // ADDITIVE (v1.9.21, T.Z101)
    filters.contract !== undefined ||
    // ADDITIVE (v1.8.0, Р9/#3, T.Y10)
    filters.managerStatus !== undefined
  );
}
